import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy } from 'react';

type AnyProps = Record<string, unknown>;

type ModuleWithDefault<T extends ComponentType<AnyProps>> = {
  default: T;
};

/**
 * Extra context this module attaches to an error object itself (never to its
 * `message`, which can end up shown to the user on the global error page) so
 * ErrorBoundary.componentDidCatch can surface it as separate root-cause context
 * on the logged payload without changing anything user-visible.
 *
 * `lazyChunkName` is the human-readable `retryKey` every `lazyWithRetry(...)` call
 * site already passes (e.g. "report-single-container", "standard-error") — this is
 * what the log's componentName/childComponentName should show instead of "app" and
 * a minified one-letter function name. Every lazy-loaded route/component in this
 * app is mounted under a single app-wide ErrorBoundary (`GlobalErrorBoundary`,
 * `name="app"`) rather than one boundary per route, so `this.props.name` in
 * ErrorBoundary is always "app" regardless of which page actually crashed, and
 * React's own componentStack frame is a minified single-letter symbol in a
 * production build — neither tells you which page/component this was. Tagging the
 * error with the same key already used for the retry-flag fixes that without
 * adding a boundary per route.
 */
export interface RetriedChunkLoadError extends Error {
  chunkRetryExhausted?: boolean;
  lazyChunkName?: string;
}

const RETRY_PREFIX = 'lazy-retry';

function getRetryStorageKey(retryKey: string): string {
  return `${RETRY_PREFIX}:${retryKey}`;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'number' || typeof error === 'boolean' || error == null) {
    return `${error ?? ''}`;
  }
  return '';
}

export function isChunkLoadError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return (
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError') ||
    message.includes('Failed to fetch dynamically imported module')
  );
}

function getRetryFlag(retryKey: string): boolean {
  try {
    return sessionStorage.getItem(getRetryStorageKey(retryKey)) === '1';
  } catch {
    return false;
  }
}

function setRetryFlag(retryKey: string): void {
  try {
    sessionStorage.setItem(getRetryStorageKey(retryKey), '1');
  } catch {
    // Ignore storage failures; app will fall through to normal error handling.
  }
}

function clearRetryFlag(retryKey: string): void {
  try {
    sessionStorage.removeItem(getRetryStorageKey(retryKey));
  } catch {
    // Ignore storage failures.
  }
}

export function lazyWithRetry<T extends ComponentType<AnyProps>>(
  importer: () => Promise<ModuleWithDefault<T>>,
  retryKey: string
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await importer();
      clearRetryFlag(retryKey);
      return module;
    } catch (error) {
      if (isChunkLoadError(error) && !getRetryFlag(retryKey)) {
        setRetryFlag(retryKey);
        window.location.reload();
        return new Promise<ModuleWithDefault<T>>(() => {});
      }
      // Read the flag before clearing it — it's only still set here when this is a
      // chunk-load error that already triggered the reload-and-retry above (on the
      // previous page load) and is now failing again for a second time.
      const alreadyRetried = isChunkLoadError(error) && getRetryFlag(retryKey);
      clearRetryFlag(retryKey);
      if (error instanceof Error) {
        const taggedError = error as RetriedChunkLoadError;
        // Tag every error from this lazy component — not just chunk-load ones — so a
        // genuine bug in the module itself (thrown at import/eval time) is just as
        // traceable to its page as a chunk-load failure is.
        taggedError.lazyChunkName = retryKey;
        if (alreadyRetried) {
          taggedError.chunkRetryExhausted = true;
        }
      }
      throw error;
    }
  });
}
